import { createAsyncThunk } from '@reduxjs/toolkit';
import { extractErrorMessage } from 'utils/helpers';

// Define a type for the wrapper function
type ThunkWrapperOptions = {
    successMessage?: string;
    errorMessage?: string;
    parseErrorMessage?: (error: unknown) => string;
    messageType?: string;
};

/**
 * Pass the ReturnType and ThunkArg type if need to pass data while dispatching the action e.g @Login action
 * and if no need to pass any argument then leave the ThunkArg and just pass the ReturnType type e.g @fetchMyUserInfo action
 */

export function createAsyncThunkWrapper<ReturnType, ThunkArg = void>(
    typePrefix: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    payloadCreator: (arg: any, thunkApi?: any) => Promise<ReturnType>,
    options: ThunkWrapperOptions = {}
) {
    const { successMessage, errorMessage, parseErrorMessage, messageType } = options;
    return createAsyncThunk<ReturnType, ThunkArg>(typePrefix, async (arg, thunkApi) => {
        try {
            const response = await payloadCreator(arg, thunkApi);
            // @ts-expect-error See the docs 'https://redux-toolkit.js.org/api/createAsyncThunk'
            return thunkApi.fulfillWithValue(response, { successMessage, messageType });
        } catch (error: any) {
            let message = null;
            const hideToast = error?.config?.headers?.hideToast;
            if (error?.message === 'Network Error') {
                message = error.message;
            } else if (errorMessage) {
                message = errorMessage;
            } else if (parseErrorMessage) {
                message = parseErrorMessage(error);
            } else if (!hideToast) {
                message = handleErrorMessage(error);
            }
            // @ts-expect-error See the docs 'https://redux-toolkit.js.org/api/createAsyncThunk'
            throw thunkApi.rejectWithValue(undefined, { errorMessage: message, messageType });
        }
    });
}

const handleErrorMessage = (error: any) => {
    console.log('$$$$$ ~ handleErrorMessage ~ error:', error);
    if (error?.response) {
        // if the response is invalid credentials means login is attempted so no need to call the refresh token api
        if (error?.response?.data?.statusMessage === 'invalid credentials') {
            return 'Invalid credentials';
        }
        if (error.response?.data?.code === 'SECURITY') {
            return error.response?.data?.statusMessage;
        }
        const msg = extractErrorMessage(error.response?.data?.message || '');
        if (msg) {
            return msg;
        }
        const data = error.response?.data;
        const additionalStatusMessage = data?.contextResponse?.additionalStatusMessage;
        const status = data?.status;
        const responseMessage = data?.error;
        const errorMessage = error?.message;
        if (additionalStatusMessage) {
            return additionalStatusMessage;
        } else if (responseMessage) {
            return responseMessage;
        } else if (errorMessage) {
            return errorMessage;
        } else if (status) {
            return status;
        }
    }
    return 'Something went wrong!';
};
